import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { SmartTemplateService } from '../smart-template.service';
import { AuthService } from 'src/app/services/auth.service';
import Swal from 'sweetalert2';

export interface SmartTemplateAssignment {
    id?: number;
    document_template_id: number;
    smart_template_group_id: number;
    priority: number;
    order_index: number;
    conditions?: any;
    is_active: boolean;
    is_default: boolean;
    created_at?: string;
    updated_at?: string;
    _isModified?: boolean;
    _expanded?: boolean;
}

export interface DocumentTemplate {
    id: number;
    name: string;
    description?: string;
    category_id: number;
    is_active: boolean;
    registry_column?: string;
    is_draft?: boolean;
    address_type?: string;
    type_id?: number; // For API compatibility
    active?: boolean; // For API compatibility
    type?: string; // For API compatibility
}

export interface SmartTemplate {
    id: number;
    name: string;
    description?: string;
    category_id?: number;
    type: string;
    is_active: boolean;
}

export interface DocumentCategory {
    id: number;
    name: string;
    description?: string;
}

export interface AssignmentStatistics {
    totalDocumentTypes: number;
    totalAssignments: number;
    assignedDocumentTypes: number;
    unassignedDocumentTypes: number;
    activeTemplates: number;
    multipleAssignments: number;
}

@Component({
    selector: 'app-smart-template-assignment',
    templateUrl: './smart-template-assignment.component.html',
    styleUrls: ['./smart-template-assignment.component.scss']
})
export class SmartTemplateAssignmentComponent implements OnInit {
    isSaving = false;
    hasChanges = false;
    showStatistics = false;

    // Data collections
    documentTemplates: DocumentTemplate[] = [];
    smartTemplates: SmartTemplate[] = [];
    documentCategories: DocumentCategory[] = [];
    assignments: SmartTemplateAssignment[] = [];
    deletedAssignments: SmartTemplateAssignment[] = []; // Track deleted assignments

    // Filtered collections
    filteredDocumentTemplates: DocumentTemplate[] = [];
    currentAssignments: SmartTemplateAssignment[] = [];

    // Selected values
    selectedDocumentTemplate: DocumentTemplate | null = null;
    selectedDocumentTemplateId: number | null = null;
    selectedCategoryId: string = '';
    selectedTemplateId: string = '';
    selectedStatus: string = '';

    // Filter options
    showOnlyUnassigned = false;

    // Statistics
    statisticsData: AssignmentStatistics = {
        totalDocumentTypes: 0,
        totalAssignments: 0,
        assignedDocumentTypes: 0,
        unassignedDocumentTypes: 0,
        activeTemplates: 0,
        multipleAssignments: 0
    };

    constructor(
        public dialogRef: MatDialogRef<SmartTemplateAssignmentComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private smartTemplateService: SmartTemplateService,
        private authService: AuthService
    ) {
        this.processToken();
    }

    ngOnInit(): void {
        this.loadInitialData();
    }

    onClose(): void {
        this.dialogRef.close({});
    }

    /**
     * Process authentication token
     */
    processToken(): void {
        const tokenObj = this.authService.returnTokenUser();
        tokenObj.then((token) => {
            this.smartTemplateService.setToken(token);
        });
    }

    /**
     * Load all initial data
     */
    loadInitialData(): void {
        console.log('Starting loadInitialData...');

        // Add individual promise logging to identify which one fails
        const documentTemplatesPromise = this.loadDocumentTemplates()
            .then(() => console.log('✓ Document templates loaded successfully'))
            .catch(error => {
                console.error('✗ Failed to load document templates:', error);
                throw new Error('Document Templates: ' + error);
            });

        const smartTemplatesPromise = this.loadSmartTemplates()
            .then(() => console.log('✓ Smart templates loaded successfully'))
            .catch(error => {
                console.error('✗ Failed to load smart templates:', error);
                throw new Error('Smart Templates: ' + error);
            });

        const documentCategoriesPromise = this.loadDocumentCategories()
            .then(() => console.log('✓ Document categories loaded successfully'))
            .catch(error => {
                console.error('✗ Failed to load document categories:', error);
                throw new Error('Document Categories: ' + error);
            });

        const assignmentsPromise = this.loadAssignments()
            .then(() => console.log('✓ Assignments loaded successfully'))
            .catch(error => {
                console.error('✗ Failed to load assignments:', error);
                throw new Error('Assignments: ' + error);
            });

        Promise.all([
            documentTemplatesPromise,
            smartTemplatesPromise,
            documentCategoriesPromise,
            assignmentsPromise
        ]).then(() => {
            console.log('✓ All data loaded successfully, applying filters...');
            this.applyFilters();
            this.calculateStatistics();
            console.log('✓ Initial data loading completed');
        }).catch(error => {
            console.error('✗ Error loading initial data:', error);
            Swal.fire({
                title: 'Eroare',
                text: `Nu s-au putut încărca datele necesare: ${error.message}`,
                icon: 'error'
            });
        });
    }

    /**
     * Load document templates
     */
    loadDocumentTemplates(): Promise<void> {
        return new Promise((resolve, reject) => {
            console.log('🔄 Loading document templates...');
            this.smartTemplateService.getDocumentTypes().subscribe(
                (response: any) => {
                    console.log('📡 Document templates API response:', response);
                    if (response && (response.errors === false || (response.errors === undefined && response.data)) && response.data) {
                        // Handle DataTables response structure where actual data is in response.data.data
                        const templateData = response.data.data || response.data;
                        const templates = Array.isArray(templateData) ? templateData : [];

                        // Map API fields to expected interface fields
                        this.documentTemplates = templates.map((template: any) => ({
                            id: template.id,
                            name: template.name,
                            description: template.description,
                            category_id: template.type_id || template.primary_category_id || 0, // Map type_id to category_id
                            is_active: template.active !== undefined ? template.active : true,
                            registry_column: template.registry_name,
                            is_draft: template.registru_necessary,
                            address_type: template.is_address ? 'address' : 'standard',
                            type_id: template.type_id,
                            active: template.active,
                            type: template.type
                        }));

                        console.log('✅ Document templates data (mapped):', this.documentTemplates);
                        resolve();
                    } else {
                        console.log('❌ Invalid document templates response structure:', response);
                        reject(`Invalid response structure. Errors: ${response?.errors}, Data: ${response?.data ? 'exists' : 'missing'}`);
                    }
                },
                (error) => {
                    console.log('❌ Document templates API error:', error);
                    reject(`API Error: ${error.message || error}`);
                }
            );
        });
    }

    /**
     * Load smart templates
     */
    loadSmartTemplates(): Promise<void> {
        return new Promise((resolve, reject) => {
            console.log('🔄 Loading smart templates...');
            this.smartTemplateService.getSmartTemplateGroups().subscribe(
                (response: any) => {
                    console.log('📡 Smart templates API response:', response);
                    if (response && response.success && response.data) {
                        // Filter only active templates and map the data properly
                        this.smartTemplates = response.data
                            .filter((template: any) => template.active || template.is_active)
                            .map((template: any) => ({
                                id: template.id,
                                name: template.name,
                                description: template.description,
                                type: template.type || 'standard',
                                category_id: template.primary_category_id,
                                is_active: template.active || template.is_active
                            }));
                        console.log('✅ Smart templates data (filtered active):', this.smartTemplates);
                        console.log(`✅ Loaded ${this.smartTemplates.length} active smart templates`);
                        resolve();
                    } else {
                        console.log('❌ Invalid smart templates response structure:', response);
                        reject(`Invalid response structure. Success: ${response?.success}, Data: ${response?.data ? 'exists' : 'missing'}`);
                    }
                },
                (error) => {
                    console.log('❌ Smart templates API error:', error);
                    reject(`API Error: ${error.message || error}`);
                }
            );
        });
    }

    /**
     * Load document categories
     */
    loadDocumentCategories(): Promise<void> {
        return new Promise((resolve, reject) => {
            console.log('🔄 Loading document categories...');
            this.smartTemplateService.getCategories().subscribe(
                (response: any) => {
                    console.log('📡 Document categories API response:', response);
                    if (response && (response.errors === false || (response.errors === undefined && response.data)) && response.data) {
                        this.documentCategories = response.data;
                        console.log('✅ Document categories data:', this.documentCategories);
                        resolve();
                    } else {
                        console.log('❌ Invalid document categories response structure:', response);
                        reject(`Invalid response structure. Errors: ${response?.errors}, Data: ${response?.data ? 'exists' : 'missing'}`);
                    }
                },
                (error) => {
                    console.log('❌ Document categories API error:', error);
                    reject(`API Error: ${error.message || error}`);
                }
            );
        });
    }

    /**
     * Load template assignments
     */
    loadAssignments(): Promise<void> {
        return new Promise((resolve, reject) => {
            console.log('🔄 Loading assignments...');
            this.smartTemplateService.getTemplateAssignments().subscribe(
                (response: any) => {
                    console.log('📡 Assignments API response:', response);
                    if (response && (response.errors === false || (response.errors === undefined && response.data)) && response.data) {
                        this.assignments = response.data;
                        console.log('✅ Assignments data:', this.assignments);
                        resolve();
                    } else {
                        console.log('❌ Invalid assignments response structure:', response);
                        reject(`Invalid response structure. Errors: ${response?.errors}, Data: ${response?.data ? 'exists' : 'missing'}`);
                    }
                },
                (error) => {
                    console.log('❌ Assignments API error:', error);
                    reject(`API Error: ${error.message || error}`);
                }
            );
        });
    }

    /**
     * Apply filters to document types
     */
    applyFilters(): void {
        // Safety check to ensure documentTemplates is initialized
        if (!this.documentTemplates || !Array.isArray(this.documentTemplates)) {
            console.warn('documentTemplates is not initialized or not an array:', this.documentTemplates);
            this.filteredDocumentTemplates = [];
            return;
        }

        let filtered = [...this.documentTemplates];

        // Filter by category
        if (this.selectedCategoryId) {
            filtered = filtered.filter(dt => dt.category_id.toString() === this.selectedCategoryId);
        }

        // Filter by assignment status
        if (this.selectedStatus) {
            switch (this.selectedStatus) {
                case 'assigned':
                    filtered = filtered.filter(dt => this.hasAssignment(dt.id));
                    break;
                case 'unassigned':
                    filtered = filtered.filter(dt => !this.hasAssignment(dt.id));
                    break;
                case 'multiple':
                    filtered = filtered.filter(dt => this.hasMultipleAssignments(dt.id));
                    break;
            }
        }

        // Filter by unassigned only
        if (this.showOnlyUnassigned) {
            filtered = filtered.filter(dt => !this.hasAssignment(dt.id));
        }

        this.filteredDocumentTemplates = filtered;

        // Update current assignments if document template is selected
        if (this.selectedDocumentTemplate) {
            this.updateCurrentAssignments();
        }
    }

    /**
     * Handle category change
     */
    onCategoryChange(): void {
        this.applyFilters();
    }

    /**
     * Handle document template change
     */
    onDocumentTemplateChange(): void {
        if (this.selectedDocumentTemplateId) {
            this.selectedDocumentTemplate = this.documentTemplates.find(dt => dt.id?.toString() === this.selectedDocumentTemplateId?.toString()) || null;
            this.updateCurrentAssignments();
        } else {
            this.selectedDocumentTemplate = null;
            this.currentAssignments = [];
        }
    }

    /**
     * Handle template change
     */
    onTemplateChange(): void {
        this.applyFilters();
    }

    /**
     * Select document template
     */
    selectDocumentTemplate(docTemplate: DocumentTemplate): void {
        this.selectedDocumentTemplate = docTemplate;
        this.selectedDocumentTemplateId = docTemplate.id;
        this.updateCurrentAssignments();
    }

    /**
     * Update current assignments for selected document template
     */
    updateCurrentAssignments(): void {
        if (this.selectedDocumentTemplate) {
            this.currentAssignments = this.assignments
                .filter(assignment => assignment.document_template_id === this.selectedDocumentTemplate!.id)
                .sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
        }
    }

    /**
     * Check if document type has assignment
     */
    hasAssignment(documentTypeId: number): boolean {
        return this.assignments.some(assignment =>
            assignment.document_template_id === documentTypeId && assignment.is_active
        );
    }

    /**
     * Check if document type has multiple assignments
     */
    hasMultipleAssignments(documentTypeId: number): boolean {
        const count = this.assignments.filter(assignment =>
            assignment.document_template_id === documentTypeId && assignment.is_active
        ).length;
        return count > 1;
    }

    /**
     * Get assignment count for document type
     */
    getAssignmentCount(documentTypeId: number): number {
        return this.assignments.filter(assignment =>
            assignment.document_template_id === documentTypeId
        ).length;
    }

    /**
     * Get last modified date for document type assignments
     */
    getLastModified(documentTypeId: number): Date | null {
        const assignments = this.assignments.filter(assignment =>
            assignment.document_template_id === documentTypeId
        );

        if (assignments.length === 0) return null;

        const lastModified = assignments.reduce((latest, assignment) => {
            const assignmentDate = new Date(assignment.updated_at || assignment.created_at || '');
            return assignmentDate > latest ? assignmentDate : latest;
        }, new Date(0));

        return lastModified;
    }

    /**
     * Get category name by ID
     */
    getCategoryName(categoryId: number): string {
        const category = this.documentCategories.find(cat => cat.id === categoryId);
        return category ? category.name : 'Necategorizat';
    }

    /**
     * Get template name by ID
     */
    getTemplateName(templateId: number): string {
        if (templateId === 0) {
            return 'Selectează smart template...';
        }
        const template = this.smartTemplates.find(tmpl => tmpl.id === templateId);
        return template ? template.name : 'Template necunoscut';
    }

    /**
     * Get template type by ID
     */
    getTemplateType(templateId: number): string {
        const template = this.smartTemplates.find(tmpl => tmpl.id === templateId);
        return template ? template.type : 'Necunoscut';
    }

    /**
     * Get priority label
     */
    getPriorityLabel(priority: number): string {
        const labels: { [key: number]: string } = {
            1: 'Scăzută',
            2: 'Normală',
            3: 'Ridicată',
            4: 'Critică'
        };
        return labels[priority] || 'Necunoscută';
    }

    /**
     * Get condition summary
     */
    getConditionSummary(conditions: string): string {
        try {
            const conditionsObj = JSON.parse(conditions);
            const keys = Object.keys(conditionsObj);
            return keys.length > 0 ? `${keys.length} condiție/condiții` : 'Fără condiții';
        } catch {
            return 'Condiții nevalide';
        }
    }

    /**
     * Add new assignment
     */
    addAssignment(): void {
        if (!this.selectedDocumentTemplate) {
            console.warn('No document template selected for assignment');
            return;
        }

        console.log('Adding new assignment for document template:', this.selectedDocumentTemplate.name);

        const newAssignment: SmartTemplateAssignment = {
            document_template_id: this.selectedDocumentTemplate.id,
            smart_template_group_id: 0, // Will be selected by user
            priority: 2,
            order_index: this.currentAssignments.length,
            conditions: {},
            is_active: true,
            is_default: false,
            _isModified: true,
            _expanded: true
        };

        this.currentAssignments.push(newAssignment);
        this.assignments.push(newAssignment);
        this.markAsModified(newAssignment);

        console.log('New assignment added:', newAssignment);
        console.log('Current assignments count:', this.currentAssignments.length);
        console.log('Available smart templates:', this.smartTemplates.length);
    }

    /**
     * Edit assignment
     */
    editAssignment(assignment: SmartTemplateAssignment, index: number): void {
        assignment._expanded = !assignment._expanded;
    }

    /**
     * Remove assignment
     */
    removeAssignment(index: number): void {
        Swal.fire({
            title: 'Confirmare ștergere',
            text: 'Sigur doriți să ștergeți această asignare?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Șterge',
            cancelButtonText: 'Anulează'
        }).then((result) => {
            if (result.isConfirmed) {
                const assignment = this.currentAssignments[index];

                console.log('Removing assignment:', assignment);

                // If assignment has an ID (exists in database), track it for deletion
                if (assignment.id && assignment.id > 0) {
                    console.log('Assignment has ID, adding to deletedAssignments for server deletion');
                    this.deletedAssignments.push(assignment);
                } else {
                    console.log('Assignment has no ID, was only local - removing locally');
                }

                // Remove from current assignments
                this.currentAssignments.splice(index, 1);

                // Remove from main assignments array
                const mainIndex = this.assignments.findIndex(a => a === assignment);
                if (mainIndex > -1) {
                    this.assignments.splice(mainIndex, 1);
                }

                this.hasChanges = true;
                this.calculateStatistics();

                console.log('Assignment removed. Deleted assignments to sync:', this.deletedAssignments.length);
            }
        });
    }

    /**
     * Mark assignment as modified
     */
    markAsModified(assignment: SmartTemplateAssignment): void {
        assignment._isModified = true;
        this.hasChanges = true;
    }

    /**
     * Save assignments
     */
    saveAssignments(): void {
        const modifiedAssignments = this.assignments.filter(assignment => assignment._isModified);
        const hasModifications = modifiedAssignments.length > 0;
        const hasDeletions = this.deletedAssignments.length > 0;

        if (!hasModifications && !hasDeletions) {
            Swal.fire({
                title: 'Info',
                text: 'Nu există modificări de salvat.',
                icon: 'info'
            });
            return;
        }

        // Validate that all assignments have a smart template selected
        const invalidAssignments = modifiedAssignments.filter(assignment =>
            assignment.smart_template_group_id === 0
        );

        if (invalidAssignments.length > 0) {
            Swal.fire({
                title: 'Eroare de validare',
                text: 'Toate asignările trebuie să aibă un smart template selectat.',
                icon: 'error'
            });
            return;
        }

        console.log('Saving changes:');
        console.log('- Modified assignments:', modifiedAssignments.length);
        console.log('- Deleted assignments:', this.deletedAssignments.length);

        this.isSaving = true;

        // Handle both save and delete operations
        this.processAssignmentChanges(modifiedAssignments);
    }

    /**
     * Process assignment changes (save and delete)
     */
    private async processAssignmentChanges(modifiedAssignments: SmartTemplateAssignment[]): Promise<void> {
        try {
            // First, delete assignments that need to be removed
            if (this.deletedAssignments.length > 0) {
                console.log('Processing deletions...');
                for (const deletedAssignment of this.deletedAssignments) {
                    if (deletedAssignment.id) {
                        await this.deleteAssignmentFromServer(deletedAssignment.id);
                    }
                }
                console.log('All deletions processed successfully');
            }

            // Then, save/update modified assignments
            if (modifiedAssignments.length > 0) {
                console.log('Processing modifications...');
                await this.saveModifiedAssignments(modifiedAssignments);
                console.log('All modifications processed successfully');
            }

            // Clear tracking arrays and update UI
            this.deletedAssignments = [];
            modifiedAssignments.forEach(assignment => {
                assignment._isModified = false;
            });

            this.hasChanges = false;
            this.calculateStatistics();
            this.isSaving = false;

            Swal.fire({
                title: 'Succes',
                text: 'Modificările au fost salvate cu succes!',
                icon: 'success'
            });

        } catch (error) {
            this.isSaving = false;
            console.error('Error processing assignment changes:', error);

            Swal.fire({
                title: 'Eroare',
                text: 'A apărut o eroare la salvarea modificărilor.',
                icon: 'error'
            });
        }
    }

    /**
     * Delete assignment from server
     */
    private deleteAssignmentFromServer(assignmentId: number): Promise<void> {
        return new Promise((resolve, reject) => {
            this.smartTemplateService.deleteTemplateAssignment(assignmentId).subscribe(
                (response: any) => {
                    if (response.success) {
                        console.log(`Assignment ${assignmentId} deleted successfully`);
                        resolve();
                    } else {
                        console.error(`Failed to delete assignment ${assignmentId}:`, response.message);
                        reject(new Error(response.message || 'Delete failed'));
                    }
                },
                (error) => {
                    console.error(`Error deleting assignment ${assignmentId}:`, error);
                    reject(error);
                }
            );
        });
    }

    /**
     * Save modified assignments to server
     */
    private saveModifiedAssignments(modifiedAssignments: SmartTemplateAssignment[]): Promise<void> {
        return new Promise((resolve, reject) => {
            this.smartTemplateService.saveTemplateAssignments(modifiedAssignments).subscribe(
                (response: any) => {
                    if (response.success) {
                        // Update assignments with server response
                        modifiedAssignments.forEach(assignment => {
                            if (!assignment.id && response.data.created) {
                                const created = response.data.created.find((c: any) =>
                                    c.document_template_id === assignment.document_template_id &&
                                    c.smart_template_group_id === assignment.smart_template_group_id
                                );
                                if (created) {
                                    assignment.id = created.id;
                                    assignment.created_at = created.created_at;
                                    assignment.updated_at = created.updated_at;
                                }
                            }
                        });
                        resolve();
                    } else {
                        reject(new Error(response.message || 'Save failed'));
                    }
                },
                (error) => {
                    reject(error);
                }
            );
        });
    }

    /**
     * Refresh assignments
     */
    refreshAssignments(): void {
        // Clear tracking arrays
        this.deletedAssignments = [];
        this.hasChanges = false;

        // Reload all data
        this.loadInitialData();
    }

    /**
     * Calculate statistics
     */
    calculateStatistics(): void {
        // Safety checks to ensure arrays are initialized
        if (!this.documentTemplates || !Array.isArray(this.documentTemplates)) {
            console.warn('documentTemplates is not initialized for statistics calculation');
            this.documentTemplates = [];
        }
        if (!this.assignments || !Array.isArray(this.assignments)) {
            console.warn('assignments is not initialized for statistics calculation');
            this.assignments = [];
        }
        if (!this.smartTemplates || !Array.isArray(this.smartTemplates)) {
            console.warn('smartTemplates is not initialized for statistics calculation');
            this.smartTemplates = [];
        }

        this.statisticsData = {
            totalDocumentTypes: this.documentTemplates.length,
            totalAssignments: this.assignments.length,
            assignedDocumentTypes: this.documentTemplates.filter(dt => this.hasAssignment(dt.id)).length,
            unassignedDocumentTypes: this.documentTemplates.filter(dt => !this.hasAssignment(dt.id)).length,
            activeTemplates: this.smartTemplates.filter(tmpl => tmpl.is_active).length,
            multipleAssignments: this.documentTemplates.filter(dt => this.hasMultipleAssignments(dt.id)).length
        };
    }

    /**
     * Toggle statistics panel
     */
    toggleStatistics(): void {
        this.showStatistics = !this.showStatistics;
    }

    /**
     * Track function for document templates
     */
    trackDocumentTemplate(index: number, item: DocumentTemplate): number {
        return item.id;
    }

    /**
     * Close dialog
     */
    close(): void {
        if (this.hasChanges) {
            Swal.fire({
                title: 'Modificări nesalvate',
                text: 'Aveți modificări nesalvate. Sigur doriți să ieșiți?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Da, ieși',
                cancelButtonText: 'Rămâi'
            }).then((result) => {
                if (result.isConfirmed) {
                    this.dialogRef.close({ success: false });
                }
            });
        } else {
            this.dialogRef.close({ success: false });
        }
    }
}